import React, { useEffect, useState, useMemo } from 'react';
import { Card, Form, Input, Button, Select, Typography, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/apiClient';
import { getUserInterfacesFromStorage, expandInterfacesWithChildren, hasRole } from '../utils/permissionUtils';

const { Title } = Typography;

const UsageBillingSettingsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const interfaces = await getUserInterfacesFromStorage(true);
      const expanded = expandInterfacesWithChildren(interfaces || []);
      const allowed =
        hasRole(userInfo, 'Tenant_Admin') &&
        expanded.includes('admin.usageBilling.settings');
      if (!allowed) {
        navigate('/dashboard', { replace: true });
        return;
      }
      try {
        const res = await apiClient.get('/api/companies');
        if (!cancelled && Array.isArray(res.data)) {
          setCompanies(res.data);
          if (res.data.length > 0) {
            const firstId = res.data[0].id ?? res.data[0].Id;
            setSelectedCompanyId(firstId);
          }
        }
      } catch {
        message.error(t('usageBilling.loadCompaniesFailed') || '無法載入公司列表');
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, userInfo, t]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoadingSettings(true);
      try {
        const res = await apiClient.get(`/api/usage-billing/settings/${selectedCompanyId}`);
        if (cancelled) return;
        form.setFieldsValue({
          currency: res.data.currency || 'HKD',
          metaTemplateOutboundUnitPrice: res.data.metaTemplateOutboundUnitPrice,
          aiCallUnitPrice: res.data.aiCallUnitPrice,
          workflowExecutionUnitPrice: res.data.workflowExecutionUnitPrice,
        });
      } catch {
        message.error(t('usageBilling.loadSettingsFailed') || '無法載入計價設定');
      } finally {
        if (!cancelled) setLoadingSettings(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, form, t]);

  const onFinish = async (values) => {
    if (!selectedCompanyId) return;
    setSaving(true);
    try {
      await apiClient.put(`/api/usage-billing/settings/${selectedCompanyId}`, {
        currency: values.currency,
        metaTemplateOutboundUnitPrice: values.metaTemplateOutboundUnitPrice,
        aiCallUnitPrice: values.aiCallUnitPrice,
        workflowExecutionUnitPrice: values.workflowExecutionUnitPrice,
      });
      message.success(t('usageBilling.saveSuccess') || '已儲存');
    } catch {
      message.error(t('usageBilling.saveFailed') || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loadingCompanies) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 560 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        {t('usageBilling.settingsTitle') || '用量計費單價設定'}
      </Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 8 }}>{t('usageBilling.selectCompany') || '公司'}</span>
          <Select
            showSearch
            optionFilterProp="children"
            style={{ minWidth: 280 }}
            value={selectedCompanyId}
            onChange={setSelectedCompanyId}
            loading={loadingSettings}
          >
            {companies.map((c) => {
              const cid = c.id ?? c.Id;
              return (
                <Select.Option key={cid} value={cid}>
                  {c.name || c.Name || cid}
                </Select.Option>
              );
            })}
          </Select>
        </div>
        <Spin spinning={loadingSettings}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item name="currency" label={t('usageBilling.currency') || '幣別'}>
              <Input maxLength={10} />
            </Form.Item>
            <Form.Item
              name="metaTemplateOutboundUnitPrice"
              label={t('usageBilling.metaTemplatePrice') || 'Meta 對外（每條）'}
              rules={[{ required: true, message: t('common.required') || '必填' }]}
            >
              <Input type="number" step="0.0001" />
            </Form.Item>
            <Form.Item
              name="aiCallUnitPrice"
              label={t('usageBilling.aiCallPrice') || 'AI（每次）'}
              rules={[{ required: true, message: t('common.required') || '必填' }]}
            >
              <Input type="number" step="0.0001" />
            </Form.Item>
            <Form.Item
              name="workflowExecutionUnitPrice"
              label={t('usageBilling.workflowExecPrice') || '流程執行（每次）'}
              rules={[{ required: true, message: t('common.required') || '必填' }]}
            >
              <Input type="number" step="0.0001" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                {t('preferences.save') || '儲存'}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default UsageBillingSettingsPage;
